/**
 * The plan banner in the console shell (plan-020 **WAPP-BILL**; design §32.12.3 Q-25, Q-26, §32.17 "Banner"; R-APP-4).
 *
 * While the organization's plan renewal is unpaid it says so on every page, for a finance reader:
 *
 *   - `past_due` (the 3-day grace): the plan's prices continue until the grace ends; this month's included minutes
 *     start once the person pays (Q-25);
 *   - `suspended` (after the grace, until the period ends): new conversations use pay-as-you-go prices until paid (Q-26).
 *
 * "Pay now" is one click from paying: it asks the API for a fresh processor's page for the renewal waiting for the
 * person (`POST /v1/billing-operations/{pay_operation_id}/pay`) and sends the browser there; when no page comes back the
 * pay page (`/billing?pay=`) shows where the payment stands. "Replace card" opens Billing's card replacement. Both need
 * `account.finance.manage`; anyone else sees the words and the way to Billing. Mounted by `shell/billing-alert.tsx`.
 */
import { useNavigate } from "@tanstack/react-router";
import { Button, Icon } from "@wetalk/ui";
import type { ReactNode } from "react";

import { portalText, usePortalTranslation } from "../../i18n/portal.js";
import { GLYPH } from "../../shell/glyph.js";
import { planAlertCopyOf, useSharedSubscription, usePayOperation } from "./plan-billing.js";

import "./plan-renewal-alert.css";

export function PlanRenewalAlert(): ReactNode {
  usePortalTranslation("billing");
  usePortalTranslation("subscription");
  usePortalTranslation("shell");
  const navigate = useNavigate();
  const shared = useSharedSubscription();
  const pay = usePayOperation((view) => {
    void navigate({ to: "/billing", search: { pay: view.operationId } });
  });
  const held = shared.held ?? null;
  const copy = planAlertCopyOf(held);
  if (held === null || copy === null) return null;
  const payOperationId = held.payOperationId;
  return (
    <div className={copy.tone === "critical" ? "c-alert c-alert-critical bl-plan-alert" : "c-alert c-alert-low bl-plan-alert"} role="status">
      <Icon glyph={GLYPH.warningCircle} size="xl" tone={copy.tone} />
      <span className="c-alert-body">
        <span className="c-alert-title">{copy.title}</span>
        <span className="c-alert-dek">{copy.dek}</span>
      </span>
      <span className="c-alert-actions">
        {shared.canManage && payOperationId !== null ? (
          <Button
            variant="primary"
            disabled={pay.isPending}
            onClick={() => {
              pay.mutate(payOperationId, {
                onError: () => {
                  void navigate({ to: "/billing", search: { pay: payOperationId } });
                },
              });
            }}
          >
            {portalText("billing", "plan.pay_now")}
          </Button>
        ) : null}
        {shared.canManage ? (
          <Button
            variant="secondary"
            onClick={() => {
              void navigate({ to: "/billing", search: { card: "replace" } });
            }}
          >
            {portalText("billing", "plan.replace_card")}
          </Button>
        ) : (
          <Button
            variant="ghost"
            onClick={() => {
              void navigate({ to: "/billing" });
            }}
          >
            {portalText("shell", "billingAlert.billing")}
          </Button>
        )}
      </span>
    </div>
  );
}
